<template>
  <v-container fluid class="form-response-container pa-0">
    <!-- Modern Header -->
    <div
      v-if="showHeader"
      class="header-section mb-6"
      style="margin: 0 12px; position: relative;"
    >
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-file-document-edit</v-icon>
        </div>
        <div class="flex-grow-1">
          <h1 class="page-title mb-1">{{ formName }}</h1>
          <p class="page-subtitle mb-0">
            {{ formDesc || "Complete the form below" }}
          </p>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-clipboard-text</v-icon
            >
            <span>Form Response</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-check-circle</v-icon
            >
            <span>Submit Data</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Loader -->
    <v-row v-if="loader" class="justify-center ma-0 px-3">
      <v-col xl="8" lg="10" md="12">
        <v-skeleton-loader
          class="mb-3"
          type="card-heading, list-item-two-line"
        />
        <v-skeleton-loader class="mb-3" type="article, actions" />
      </v-col>
    </v-row>

    <!-- Main Content -->
    <v-row v-else class="justify-center ma-0 px-3">
      <v-col xl="8" lg="10" md="12" class="pa-2">
        <!-- Tabs -->
        <v-card class="modern-card customBorderRadius mb-4" elevation="0">
          <v-tabs v-model="tab" color="primary" centered>
            <v-tab v-if="responses.length > 0" class="text-capitalize">
              <v-icon left>mdi-history</v-icon>
              Earlier Responses
            </v-tab>
            <v-tab class="text-capitalize">
              <v-icon left>mdi-plus-circle</v-icon>
              New Response
            </v-tab>
          </v-tabs>
        </v-card>

        <v-tabs-items v-model="tab" class="transparent">
          <!-- Earlier Responses Tab -->
          <v-tab-item v-if="responses.length > 0" class="transparent">
            <v-expansion-panels accordion>
              <v-expansion-panel
                v-for="(res, i) in responses"
                :key="i"
                class="modern-card customBorderRadius mb-3"
              >
                <v-expansion-panel-header class="card-header-light">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3"
                      >mdi-file-document-check</v-icon
                    >
                    <span class="font-weight-medium">
                      Response submitted on
                      {{ new Date(res.createdOn).toISOString().substr(0, 10) }}
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-4">
                  <div v-for="(question, i) in questionSet" :key="i">
                    <v-card
                      class="question-card customBorderRadius mb-3"
                      elevation="0"
                      outlined
                    >
                      <v-card-title class="question-title pb-2">
                        {{ question.name }}
                        <span v-if="question.required" class="red--text ml-1"
                          >*</span
                        >
                      </v-card-title>
                      <!-- If the datatype is text -->
                      <v-card-text
                        v-if="question.datatype == 'text'"
                        class="py-0"
                      >
                        <v-row class="my-0 py-0">
                          <v-col cols="8" class="py-0">
                            <v-text-field
                              v-model="res.data[i]"
                              :rules="question.required ? requiredRule : []"
                              class="text-body-2 pb-2"
                              placeholder="Your answer"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <!-- If the datatype is mcq -->
                      <v-card-text
                        v-if="question.datatype == 'mcq'"
                        class="py-0"
                      >
                        <v-radio-group v-model="res.data[i]" row>
                          <v-radio
                            v-for="(option, j) in question.options"
                            :key="j"
                            class="text-body-2"
                            :label="option"
                            :value="option"
                          />
                        </v-radio-group>
                      </v-card-text>
                      <!-- If the datatype is checkbox -->
                      <v-card-text
                        v-if="question.datatype == 'checkbox'"
                        class="py-0 d-flex"
                      >
                        <v-checkbox
                          v-for="(option, j) in question.options"
                          :key="j"
                          v-model="res.data[i]"
                          class="pr-4 text-body-2"
                          :label="option"
                          :value="option"
                        />
                      </v-card-text>
                      <!-- If the datatype is dropdown -->
                      <v-card-text
                        v-if="question.datatype == 'dropdown'"
                        class="py-0 d-flex"
                      >
                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              class="my-2 text-body-2"
                              color="grey"
                              outlined
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ res.data[i] == "" ? "Select" : res.data[i] }}
                              <v-icon right>mdi-chevron-down</v-icon>
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item
                              v-for="(option, j) in question.options"
                              :key="j"
                              @click="res.data[i] = option"
                            >
                              <v-list-item-title>{{
                                option
                              }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-card-text>
                      <!-- If the datatype is file -->
                      <v-card-text
                        v-if="question.datatype == 'file'"
                        class="py-0"
                      >
                        <v-row class="my-0 py-0">
                          <v-col cols="8" class="py-0">
                            <v-file-input
                              class="text-body-2 pb-2"
                              :rules="question.required ? requiredRule : null"
                              :accept="
                                question.allowfileType
                                  ? question.filetype
                                  : null
                              "
                              show-size
                              :placeholder="
                                res.data[i]
                                  ? res.data[i].attachmentname
                                  : 'Add File'
                              "
                              dense
                              @change="onFileSelect(i, $event)"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <!-- If the datatype is date -->
                      <v-card-text
                        v-if="question.datatype == 'date'"
                        class="py-0"
                      >
                        <v-row class="my-0 py-0">
                          <v-col cols="8" class="py-0">
                            <v-menu
                              :close-on-content-click="false"
                              :nudge-right="40"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="res.data[i]"
                                  :rules="question.required ? requiredRule : []"
                                  class="text-body-2"
                                  placeholder="Your Date"
                                  prepend-icon="mdi-calendar"
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <v-date-picker
                                v-model="res.data[i]"
                                color="primarygrad"
                              />
                            </v-menu>
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <!-- If the datatype is time -->
                      <v-card-text
                        v-if="question.datatype == 'time'"
                        class="py-0"
                      >
                        <v-row class="my-0 py-0">
                          <v-col cols="8" class="py-0">
                            <v-menu
                              :close-on-content-click="false"
                              :nudge-right="40"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="res.data[i]"
                                  :rules="question.required ? requiredRule : []"
                                  class="text-body-2"
                                  placeholder="Your Time"
                                  prepend-icon="mdi-clock-time-four-outline"
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <v-time-picker
                                v-model="res.data[i]"
                                ampm-in-title
                                color="primarygrad"
                              />
                            </v-menu>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </div>
                  <v-divider class="my-3"></v-divider>
                  <div class="d-flex pt-2">
                    <v-spacer></v-spacer>
                    <v-btn
                      outlined
                      color="error"
                      style="border-radius: 8px;"
                      @click="remove(res)"
                    >
                      <v-icon left small>mdi-delete</v-icon>
                      Delete
                    </v-btn>
                    <v-btn
                      color="primary"
                      depressed
                      class="ml-3"
                      style="border-radius: 8px;"
                      @click="update(res)"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Update
                    </v-btn>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-tab-item>

          <!-- New Response Tab -->
          <v-tab-item>
            <div v-for="(question, i) in questionSet" :key="i">
              <v-card
                class="question-card customBorderRadius mb-3"
                elevation="0"
                outlined
              >
                <v-card-title class="question-title pb-2">
                  {{ question.name }}
                  <span v-if="question.required" class="red--text ml-1">*</span>
                </v-card-title>
                <!-- If the datatype is text -->
                <v-card-text v-if="question.datatype == 'text'" class="py-0">
                  <v-row class="my-0 py-0">
                    <v-col cols="8" class="py-0">
                      <v-text-field
                        v-model="answerSet[i]"
                        :rules="question.required ? requiredRule : []"
                        class="text-body-2 pb-2"
                        placeholder="Your answer"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <!-- If the datatype is mcq -->
                <v-card-text v-if="question.datatype == 'mcq'" class="py-0">
                  <v-radio-group v-model="answerSet[i]" row>
                    <v-radio
                      v-for="(option, j) in question.options"
                      :key="j"
                      class="text-body-2"
                      :label="option"
                      :value="option"
                    />
                  </v-radio-group>
                </v-card-text>
                <!-- If the datatype is checkbox -->
                <v-card-text
                  v-if="question.datatype == 'checkbox'"
                  class="py-0 d-flex"
                >
                  <v-checkbox
                    v-for="(option, j) in question.options"
                    :key="j"
                    v-model="answerSet[i]"
                    class="pr-4 text-body-2"
                    :label="option"
                    :value="option"
                  />
                </v-card-text>
                <!-- If the datatype is dropdown -->
                <v-card-text
                  v-if="question.datatype == 'dropdown'"
                  class="py-0 d-flex"
                >
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="my-2 text-body-2"
                        color="grey"
                        outlined
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ answerSet[i] == "" ? "Select" : answerSet[i] }}
                        <v-icon right>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="(option, j) in question.options"
                        :key="j"
                        @click="answerSet[i] = option"
                      >
                        <v-list-item-title>{{ option }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-card-text>
                <!-- If the datatype is file -->
                <v-card-text v-if="question.datatype == 'file'" class="py-0">
                  <v-row class="my-0 py-0">
                    <v-col cols="8" class="py-0">
                      <v-file-input
                        class="text-body-2 pb-2"
                        :rules="question.required ? requiredRule : null"
                        :accept="
                          question.allowfileType ? question.filetype : null
                        "
                        show-size
                        :placeholder="
                          answerSet[i]
                            ? answerSet[i].attachmentname
                            : 'Add File'
                        "
                        dense
                        @change="onFileSelect(i, $event)"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <!-- If the datatype is date -->
                <v-card-text v-if="question.datatype == 'date'" class="py-0">
                  <v-row class="my-0 py-0">
                    <v-col cols="8" class="py-0">
                      <v-menu
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="answerSet[i]"
                            :rules="question.required ? requiredRule : []"
                            class="text-body-2"
                            placeholder="Your Date"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <v-date-picker
                          v-model="answerSet[i]"
                          color="primarygrad"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
                <!-- If the datatype is time -->
                <v-card-text v-if="question.datatype == 'time'" class="py-0">
                  <v-row class="my-0 py-0">
                    <v-col cols="8" class="py-0">
                      <v-menu
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="answerSet[i]"
                            :rules="question.required ? requiredRule : []"
                            class="text-body-2"
                            placeholder="Your Time"
                            prepend-icon="mdi-clock-time-four-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <v-time-picker
                          v-model="answerSet[i]"
                          ampm-in-title
                          color="primarygrad"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>
            <v-card class="modern-card customBorderRadius mt-4" elevation="0">
              <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  large
                  depressed
                  style="border-radius: 8px; min-width: 140px;"
                  @click="save()"
                >
                  <v-icon left>mdi-send</v-icon>
                  Submit
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <!-- Snackbars -->
    <v-snackbar v-model="moreSpace" color="error">
      File must be less than 10MB
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="moreSpace = false">Close</v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="formatMismatch" color="error">
      This type of file is not expected
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="formatMismatch = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
const _ = require("lodash");
export default {
  data() {
    return {
      showHeader: false,
      tab: 0,
      formId: "",
      responseId: "",
      contactId: "",
      formName: "",
      formDesc: "",
      questionSet: [],
      loader: true,
      answerSet: [],
      responses: [],
      datemenu: false,
      timemenu: false,
      moreSpace: false,
      formatMismatch: false,
      datemenuNew: false,
      timemenuNew: false,
      requiredRule: [(v) => !!v || "This is a required question"],
    };
  },
  async created() {
    this.showHeader =
      localStorage.getItem("formResponseHeaderDismissed") !== "true";
    this.formId = this.$route.params.formId;
    this.contactId = this.$route.params.contactId;
    await this.getForm(this.formId);
    await this.checkFormResponse(this.contactId);
    this.loader = false;
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("formResponseHeaderDismissed", "true");
    },
    async getForm(id) {
      // getting the details of the form
      this.$http
        .get(`${this.$store.state.ENDPOINT}/forms/${id}`)
        .then((res) => {
          // setting the details of the form
          this.formName = res.data.name;
          this.formDesc = res.data.desc;
          this.questionSet = res.data.fields;
          this.prepareAns(this.questionSet);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err);
        }); //if error occurs
    },
    async checkFormResponse(contactId) {
      this.$http
        .get(`${this.$store.state.ENDPOINT}/contacts/${contactId}/formResponse`)
        .then((res) => {
          this.responses = res.body.filter((fr) => fr.formId == this.formId);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err);
        }); //if error occurs
    },
    prepareAns(questionSet) {
      this.answerSet = new Array(questionSet.length);
      for (var i = 0; i < questionSet.length; i++) {
        if (questionSet[i].datatype == "checkbox") this.answerSet[i] = [];
        else if (questionSet[i].datatype == "mcq")
          this.answerSet[i] = questionSet[i].options[0];
        else if (questionSet[i].datatype == "file") this.answerSet[i] = null;
        else this.answerSet[i] = "";
      }
      this.response.answerSet = this.answerSet;
    },
    onFileSelect(i, file) {
      if (file) {
        var mime_type = file.type;
        // var fileName = file.name;
        var size = file.size;
        console.log("file: ", file);
        console.log("this.questionSet[i]: ", this.questionSet[i]);
        console.log("this.answerSet[i]: ", this.answerSet[i]);
        // More than 10MB
        if (size / (1024 * 1024) > 10) {
          this.moreSpace = true;
          this.answerSet[i] = null;
        }
        // Not allowed file type
        else if (
          this.questionSet[i].allowfileType &&
          !_.includes(this.questionSet[i].filetype, mime_type)
        ) {
          this.formatMismatch = true;
          this.answerSet[i] = null;
        } else {
          this.answerSet[i] = file;
        }
        console.log("this.answerSet[i]: ", this.answerSet[i]);
      }
    },
    valiadtion(answerSet) {
      for (var i = 0; i < this.questionSet.length; i++) {
        if (
          this.questionSet[i].required &&
          (answerSet[i] == "" || answerSet[i] == [])
        ) {
          this.$swal({
            type: "warning",
            text: "Please fill all mandatory questions",
          });
          return false;
        } else if (
          this.questionSet[i].required &&
          (answerSet[i] == null || answerSet[i] == {})
        ) {
          this.$swal({
            type: "warning",
            text: "Please check all mandatory attachments",
          });
          return false;
        }
      }
      // for (i=0;i<this.questionSet.length;i++)
      // {
      //     if(this.questionSet[i].datatype =='file' )
      //         {
      //             console.log("this.answerSet[i]: ",answerSet[i])
      //             var formData = new FormData();
      //             formData.append("file", answerSet[i]);
      //             formData.append("type", this.questionSet[i].filetype);
      //             formData.append("companyId", this.$store.state.companyDetails.id);
      //             var config = {
      //                 headers: {
      //                     "Content-Type": "multipart/form-data",
      //                     authorization: this.$store.state.user.token,
      //                 },
      //             };
      //             var attachment = await this.$http.post(
      //             `${this.$store.state.ENDPOINT}/attachments/upload`,
      //             formData,
      //             config
      //             );
      //             attachment = attachment.body;
      //             answerSet[i]={
      //                     attachmentId: attachment.id,
      //                     attachmentname: attachment.name,
      //                 }
      //         }
      // }
      return true;
    },
    async update(res) {
      if (this.valiadtion(res.data)) {
        this.$http
          .patch(`${this.$store.state.ENDPOINT}/formResponses/${res.id}`, res) //storing the formResponse in the database
          .then((res) => {
            // console.log("Form Response: ",res )
            this.$swal({
              type: "success",
              text: "Form Response Updated",
            }).then((yes) => {
              // after storing the message is displayed
              if (yes.value) {
                // on user click redirected to forms page
                this.$router.go(-1);
              }
            });
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.log("Error in storing formResponse", err);
          }); // in case of error in storing the formResponse
      }
    },
    async save() {
      for (var i = 0; i < this.questionSet.length; i++) {
        if (
          this.questionSet[i].required &&
          (this.answerSet[i] == "" || this.answerSet[i] == [])
        )
          return this.$swal({
            type: "warning",
            text: "Please fill all mandatory questions",
          });
        else if (
          this.questionSet[i].required &&
          (this.answerSet[i] == null || this.answerSet[i] == {})
        )
          return this.$swal({
            type: "warning",
            text: "Please check all mandatory attachments",
          });
      }

      // for attachment
      // for (i=0;i<this.questionSet.length;i++)
      // {
      //     if(this.questionSet[i].datatype =='file' )
      //         {
      //             console.log("this.answerSet[i]: ",this.answerSet[i])
      //             var formData = new FormData();
      //             formData.append("file", this.answerSet[i]);
      //             formData.append("type", this.questionSet[i].filetype);
      //             formData.append("companyId", this.$store.state.companyDetails.id);
      //             var config = {
      //                 headers: {
      //                     "Content-Type": "multipart/form-data",
      //                     authorization: this.$store.state.user.token,
      //                 },
      //             };
      //             var attachment = await this.$http.post(
      //             `${this.$store.state.ENDPOINT}/attachments/upload`,
      //             formData,
      //             config
      //             );
      //             attachment = attachment.body;
      //             this.answerSet[i]={
      //                     attachmentId: attachment.id,
      //                     attachmentname: attachment.name,
      //                 }
      //         }
      // }

      var payload = {};
      payload.data = this.answerSet; //AnswerSet
      payload.questionSet = this.questionSet;
      payload.createdOn = new Date().toISOString(); //created Date
      payload.formsId = this.formId; //form Id
      payload.contactId = this.contactId; // contact Id
      payload.accountsId = this.$store.state.user.id; //account ID
      console.log("payload", payload);

      // storing new formResponse
      this.$http
        .post(`${this.$store.state.ENDPOINT}/formResponses`, payload) //storing the formResponse in the database
        .then((res) => {
          // console.log("Form Response: ",res )
          this.$swal({
            type: "success",
            text: "New Form Response is submitted",
          }).then((yes) => {
            // after storing the message is displayed
            if (yes.value) {
              // on user click redirected to forms page
              this.$router.go(-1);
            }
          });
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log("Error in storing formResponse", err);
        }); // in case of error in storing the formResponse
    },
    async remove(res) {
      this.$swal({
        type: "warning",
        text: "Are you sure you want to Delete this response",
        showCancelButton: true,
        confirmButtonColor: "#31a848",
        cancelButtonColor: "#f44336",
        confirmButtonText: "Yes, Remove it!",
      }).then((yes) => {
        if (yes.value) {
          this.$http
            .delete(`${this.$store.state.ENDPOINT}/formResponses/${res.id}`)
            .then((resp) => {
              var i = this.responses.indexOf(res);
              console.log(i);
              this.responses.splice(i, 1);
              this.$swal({
                type: "success",
                text: "Form Response is deleted",
              });
            })
            .catch((err) => {
              console.log("Error in deleting formResponse", err);
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.form-response-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 92vh;
}

.theme--dark .form-response-container {
  background-color: #121212 !important;
}

.modern-card {
  background: var(--v-background-base, white);
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.card-header-light {
  background: linear-gradient(
    135deg,
    rgba(55, 207, 55, 0.1) 0%,
    rgba(43, 168, 43, 0.1) 100%
  );
  border-bottom: 1px solid #e0e0e0;
  font-weight: 500;
}

.theme--dark .card-header-light {
  background: linear-gradient(
    135deg,
    rgba(55, 207, 55, 0.15) 0%,
    rgba(43, 168, 43, 0.15) 100%
  ) !important;
  border-color: #333333 !important;
}

.question-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.theme--dark .question-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.question-card:hover {
  border-color: #37cf37;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.1);
}

.question-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  padding: 16px;
  background: #f9f9f9;
  border-bottom: 1px solid #e0e0e0;
}

.theme--dark .question-title {
  color: #e0e0e0 !important;
  background: #2d2d2d !important;
  border-color: #333333 !important;
}
</style>

<style>
.customForm .v-text-field > .v-input__control > .v-input__slot:before {
  border-style: none !important;
}
.customForm .v-text-field > .v-input__control > .v-input__slot:after {
  border-style: none !important;
}
</style>
